import type { SdkConfig } from '../core/types';
import type { Identity } from '../core/identity';
import type { Optin } from '../core/optin';
import { isValidEmail } from '../core/email';
import { log } from '../core/log';

/**
 * GA4 / GTM dataLayer scanner — opt-in via `autoTrack.dataLayerEmail`.
 *
 * Sites running GA4 or GTM commonly publish the logged-in user's email
 * through `window.dataLayer`. We:
 *   1. Scan the existing dataLayer for known shapes.
 *   2. Monkey-patch `dataLayer.push` so future pushes get inspected too.
 *
 * Off by default because dataLayer ergonomics vary wildly between sites
 * and false positives (e.g. somebody pushing a generic `email` form
 * value) are easy. Turn on when the integrator confirms what the site
 * is publishing.
 */

const KNOWN_PATHS: Array<(o: any) => unknown> = [
	(o) => o?.email,
	(o) => o?.userEmail,
	(o) => o?.user?.email,
	(o) => o?.customer?.email,
	// GA4 enhanced conversions
	(o) => o?.user_data?.email_address,
	(o) => o?.user_data?.email,
];

function pickEmail(entry: unknown): string | null {
	if (entry == null || typeof entry !== 'object') return null;
	for (const fn of KNOWN_PATHS) {
		try {
			const v = fn(entry);
			if (typeof v === 'string' && isValidEmail(v)) return v;
		} catch {
			/* swallow — exotic getters */
		}
	}
	return null;
}

export function installDataLayer(config: SdkConfig, identity: Identity, optin: Optin): void {
	if (!config.autoTrack.dataLayerEmail) return;
	if (typeof window === 'undefined') return;

	// Hashes, persists, and records the opt-in once the hash resolves. Goes
	// through Identity rather than the public API so the source stays
	// `datalayer` instead of collapsing into the generic `api` one.
	const capture = (email: string) => {
		identity.setEmail(email).then((hash) => {
			if (hash) optin.record('datalayer');
		});
	};

	const w = window as any;
	const dl: any[] = (w.dataLayer = w.dataLayer || []);

	// 1. Scan existing entries.
	for (const entry of dl) {
		const email = pickEmail(entry);
		if (email) {
			log('datalayer: found email in existing entry');
			capture(email);
			break; // one is enough — Identity.setEmail is idempotent anyway
		}
	}

	// 2. Hook future pushes.
	if (!w.__emblueDataLayerHooked) {
		w.__emblueDataLayerHooked = true;
		const origPush = dl.push.bind(dl);
		dl.push = function (...args: any[]) {
			const result = origPush(...args);
			for (const arg of args) {
				const email = pickEmail(arg);
				if (email) {
					log('datalayer: found email in push');
					capture(email);
				}
			}
			return result;
		};
	}
}
